import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { toast } from 'sonner';
import { CalendarDays, Plus, Trash2, RefreshCw } from 'lucide-react';
import { API_URL } from '../utils/apiUrl';

export default function MarketHolidaysPage() {
  const { token } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ date: '', name: '' });

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/market-holidays`, {
        params: { year },
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(res.data.holidays || []);
    } catch (e) {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHolidays(); }, [year]);  // eslint-disable-line

  const addHoliday = async () => {
    if (!form.date || !form.name) { toast.error('Date and name required'); return; }
    try {
      await axios.post(`${API_URL}/admin/market-holidays`,
        { date: form.date, name: form.name, segment: 'equity' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Holiday added');
      setAddOpen(false);
      setForm({ date: '', name: '' });
      fetchHolidays();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const deleteHoliday = async (date) => {
    if (!window.confirm(`Delete holiday on ${date}?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/market-holidays/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const reseedNSE = async () => {
    if (!window.confirm('Re-seed NSE 2026 holidays? Existing 2026 holidays will be replaced.')) return;
    try {
      await axios.post(`${API_URL}/admin/market-holidays/seed-nse-2026?overwrite=true`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('NSE 2026 holidays re-seeded');
      setYear(2026);
      fetchHolidays();
    } catch (e) {
      toast.error('Re-seed failed');
    }
  };

  const years = [2025, 2026, 2027, 2028];

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="market-holidays-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarDays className="w-6 h-6 text-[#C5A059]" />
          <div>
            <h1 className="text-2xl font-bold text-white">Market Holidays</h1>
            <p className="text-sm text-slate-400">NSE/BSE trading holidays used for daily target &amp; backlog calculations</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28 bg-slate-800 border-slate-700 text-white" data-testid="holiday-year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={reseedNSE} variant="outline" size="sm" data-testid="reseed-nse-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Re-seed NSE 2026
          </Button>
          <Button onClick={() => setAddOpen(true)} className="bg-[#C5A059] hover:bg-[#b48d45]" data-testid="add-holiday-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Holiday
          </Button>
        </div>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Holidays ({holidays.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-slate-400 py-8">Loading…</div>
          ) : holidays.length === 0 ? (
            <div className="text-center text-slate-400 py-8">No holidays found for {year}</div>
          ) : (
            <div className="space-y-2">
              {holidays.map((h) => {
                const dt = new Date(h.date);
                const day = dt.toLocaleDateString('en-IN', { weekday: 'long' });
                return (
                  <div key={h.date} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700" data-testid={`holiday-row-${h.date}`}>
                    <div>
                      <div className="text-white font-medium">{h.name}</div>
                      <div className="text-xs text-slate-400">
                        {h.date} · {day} · {h.segment || 'equity'} {h.source === 'NSE' && <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">NSE</span>}
                      </div>
                    </div>
                    <Button onClick={() => deleteHoliday(h.date)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300" data-testid={`delete-holiday-${h.date}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add Market Holiday</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-white">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="new-holiday-date" />
            </div>
            <div>
              <Label className="text-white">Holiday Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Republic Day" className="bg-slate-800 border-slate-700 text-white" data-testid="new-holiday-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addHoliday} className="bg-[#C5A059] hover:bg-[#b48d45]" data-testid="save-holiday-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
